import React from 'react';
import { Link } from 'react-router-dom';
import { Send, Heart, Users, ScrollText, Camera, FileCheck, CheckCircle2, Star, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const LandingChurches = () => {
  const holySites = [
    { name: "Western Wall", image: "https://images.unsplash.com/photo-1695781857590-9c1ee8847a8a?w=300" },
    { name: "Church of the Holy Sepulchre", image: "https://images.unsplash.com/photo-1595133885824-a3a598947576?w=300" },
    { name: "Garden Tomb", image: "https://images.unsplash.com/photo-1642757551038-9ad1ae5f84aa?w=300" },
    { name: "Via Dolorosa", image: "https://images.unsplash.com/photo-1686950109008-5cfd6bdff925?w=300" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Church Prayer Programs | Prayers Delivered to Jerusalem Holy Sites"
        description="Submit your congregation's prayers to be printed on our Weekly Sacred Scroll and delivered to Jerusalem's holiest sites. Photo proof for every family."
        path="/churches"
      />
      
      {/* Hero Section - Bright & Warm */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-amber-200/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl" />
        
        <div className="relative z-10 container mx-auto px-5 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-200 rounded-full">
                <Users className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700 text-sm font-semibold">For Churches & Congregations</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                Your Congregation's Prayers<br />
                <span className="text-amber-600">Delivered to Jerusalem</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Every family's prayer printed on our <strong className="text-amber-700">Weekly Sacred Scroll</strong> and personally delivered to Jerusalem's holiest sites.
              </p>
              
              {/* Key Points */}
              <div className="space-y-3 mb-8">
                {[
                  "Each prayer printed on the Weekly Sacred Scroll",
                  "Delivered to the Western Wall, Holy Sepulchre & more",
                  "Photo proof for every family",
                  "Group pricing available"
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-8 py-7 shadow-lg shadow-amber-500/25 transition-all" data-testid="hero-cta-btn">
                    <Send className="mr-3" size={22} />
                    Get Church Pricing
                  </Button>
                </Link>
                <Link to="/prayer-request">
                  <Button size="lg" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 text-lg px-8 py-7">
                    Submit Individual Prayer
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/34504326/pexels-photo-34504326.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Church congregation in worship"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                  <p className="text-white font-semibold text-lg">Unite Your Congregation</p>
                  <p className="text-white/80 text-sm">Prayers delivered to the Holy Land</p>
                </div>
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">200+ Churches</p>
                    <p className="text-sm text-gray-500">Trust Our Service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                How It Works for Churches
              </h2>
              <p className="text-lg text-gray-600">Simple process, profound spiritual experience</p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { num: "1", icon: Users, title: "Collect Prayers", desc: "Gather prayers from your congregation. Each family writes their intentions.", color: "bg-blue-50 text-blue-600" },
                { num: "2", icon: ScrollText, title: "Printed on Scroll", desc: "All prayers printed on our Weekly Sacred Scroll in Jerusalem.", color: "bg-amber-50 text-amber-600" },
                { num: "3", icon: MapPin, title: "Delivered to Sites", desc: "Scroll carried to the Western Wall, Holy Sepulchre & other sacred sites.", color: "bg-green-50 text-green-600" },
                { num: "4", icon: Camera, title: "Photo Proof Sent", desc: "Each family receives photos of the scroll at holy sites.", color: "bg-purple-50 text-purple-600" }
              ].map((step, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {step.num}
                  </div>
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-xl ${step.color} flex items-center justify-center`}>
                    <step.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Holy Sites - Bright Cards */}
      <section className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto px-5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                Where Your Prayers Will Be Delivered
              </h2>
              <p className="text-lg text-gray-600">Choose from Jerusalem's most sacred sites</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {holySites.map((site, i) => (
                <div key={i} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all">
                  <img src={site.image} alt={site.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-bold text-white text-lg">{site.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Families Receive */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
                What Every Family Receives
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Camera, title: "Photo Proof", desc: "Timestamped photos of the Weekly Sacred Scroll at each holy site", color: "from-blue-500 to-blue-600" },
                { icon: FileCheck, title: "Delivery Confirmation", desc: "Email confirmation with date, time & location of each delivery", color: "from-amber-500 to-orange-500" },
                { icon: Heart, title: "Prayers Delivered", desc: "Our Jerusalem team personally carries each family's prayers to the holy sites", color: "from-pink-500 to-rose-500" }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center hover:shadow-xl transition-shadow">
                  <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial - Bright */}
      <section className="py-20 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center items-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-7 h-7 text-amber-400" fill="currentColor" />
              ))}
            </div>
            <p className="text-xl md:text-2xl text-gray-800 mb-8 italic leading-relaxed">
              "When we showed our congregation the photo of the Sacred Scroll with all their prayers at the Western Wall, it was incredibly moving. Every family received their photo proof. This service brought our church together in a beautiful way."
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                R
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-900">Pastor Robert</p>
                <p className="text-amber-600">Grace Community Church, North Carolina</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Perfect For</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {["Lent & Easter", "Christmas Season", "Church Anniversaries", "Prayer Revivals", "Youth Programs", "Memorial Services"].map((item, i) => (
                <span key={i} className="px-5 py-2.5 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 rounded-full font-medium border border-amber-200 hover:shadow-md transition-shadow">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Bright */}
      <section className="py-24 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600">
        <div className="container mx-auto px-5">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollText className="w-16 h-16 mx-auto mb-6 text-white/90" />
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6">
              Ready to Deliver Your<br />Congregation's Prayers?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Group pricing available. Photo proof for every family.
            </p>
            
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-gray-100 text-amber-600 text-xl px-12 py-8 shadow-2xl transition-all font-semibold" data-testid="final-cta-btn">
                <Send className="mr-3" size={24} />
                Get Church Pricing
              </Button>
            </Link>
            
            <p className="mt-6 text-white/80 text-sm">
              We respond within 24 hours
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-gray-900 text-center">
        <p className="text-gray-400 text-sm">
          © 2025 Jerusalem Holy Prayer • <Link to="/privacy" className="hover:text-white">Privacy</Link> • <Link to="/terms" className="hover:text-white">Terms</Link> • <Link to="/contact" className="hover:text-white">Contact</Link>
        </p>
      </footer>
    </div>
  );
};

export default LandingChurches;
